<script setup>
import {
  computed,
  defineAsyncComponent,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { MiniMap } from '@vue-flow/minimap';
import { Controls, ControlButton } from '@vue-flow/controls';
import { useAlert } from 'dashboard/composables';
import { computeAutoLayout } from '../composables/useAutoLayout';
import CommandPalette from './CommandPalette.vue';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/minimap/dist/style.css';
import '@vue-flow/controls/dist/style.css';

defineProps({
  isLoading: Boolean,
});
const emit = defineEmits(['editNode']);
// Node components are lazy to keep the initial canvas mount cheap when the
// editor is mounted off the critical path (e.g. background tab).
//
// `markRaw` is critical: Vue Flow internally puts the node-type map into
// a reactive ref. Without markRaw, Vue tries to make each component
// reactive too — generates a hot-path warning on every node render
// ("Vue received a Component that was made a reactive object") and adds
// real overhead for big graphs. markRaw is exactly the escape hatch the
// Vue warning recommends.
const TriggerNode = markRaw(
  defineAsyncComponent(() => import('./nodes/TriggerNode.vue'))
);
const ActionNode = markRaw(
  defineAsyncComponent(() => import('./nodes/ActionNode.vue'))
);
const ConditionNode = markRaw(
  defineAsyncComponent(() => import('./nodes/ConditionNode.vue'))
);
const AnnotationNode = markRaw(
  defineAsyncComponent(() => import('./nodes/AnnotationNode.vue'))
);
const SwitchNode = markRaw(
  defineAsyncComponent(() => import('./nodes/SwitchNode.vue'))
);

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

// ── Provide editNode to descendants ────────────────────────────────────────
// BaseNode injects this rather than emitting up through Vue Flow's slot
// boundary (which strips events).
provide('editNode', nodeId => emit('editNode', nodeId));

// ── Reactive bridge: Vuex store ⇄ Vue Flow ─────────────────────────────────
// The Vuex store remains the source of truth for the flow definition
// (so undo/redo, dirty-state, save/load all work unchanged). Vue Flow
// gets a synchronized copy and reports back position drags + new edges
// via events.

const storeNodes = computed(
  () => store.state.automationFlow?.flow?.nodes || []
);
const storeConnections = computed(
  () => store.state.automationFlow?.flow?.connections || []
);

// Map our store nodes → Vue Flow node shape. Vue Flow expects
// { id, type, position: {x, y}, data }.
const toVueFlowNode = node => ({
  id: node.id,
  type: node.type || 'action',
  position: node.position || { x: 0, y: 0 },
  data: node.data || {},
});

// Map our store connections → Vue Flow edge shape. Vue Flow expects
// { id, source, target, sourceHandle, targetHandle, label, animated, type }.
const toVueFlowEdge = connection => ({
  id: connection.id,
  source: connection.source,
  target: connection.target,
  sourceHandle: connection.sourceHandle || 'right',
  targetHandle: connection.targetHandle || 'left',
  label: connection.label || '',
  animated: connection.animated !== false, // marching ants by default
  type: 'smoothstep',
  selectable: true,
});

const nodes = ref(storeNodes.value.map(toVueFlowNode));
const edges = ref(storeConnections.value.map(toVueFlowEdge));

// ── Vue Flow instance ──────────────────────────────────────────────────────
// useVueFlow() with an explicit id binds to the matching <VueFlow id=...>
// child below. Without an id, both the parent and the <VueFlow> child
// would receive different default instances and event handlers would no-op.
const FLOW_ID = 'flow-builder-canvas';
const {
  onConnect,
  onNodeDragStop,
  onEdgesChange,
  onEdgeContextMenu,
  fitView,
  project,
  updateNodeData,
  findNode,
  findEdge,
} = useVueFlow(FLOW_ID);

// When the store changes (load, undo/redo, programmatic mutation),
// rebuild Vue Flow's arrays. Local position drags are reflected back to
// the store via `onNodeDragStop` below — that mutation will trigger this
// watcher, so we need a guard to avoid the loop.
//
// Perf: previously used `JSON.stringify(mapped) !== JSON.stringify(current)`
// which is O(N) string-ify + O(N) compare on every drag-stop. For 100+
// node flows that adds ~20-50ms per drag. Replaced by a length+id-hash
// signature that runs in O(N) but with no allocation of big strings.
// nodeRev() reads the per-node revision counter that the store bumps on
// data mutations — without it, a same-id+pos+type node looks identical
// even after its data payload was replaced, and SwitchNode handles /
// set_variable badges miss the update.
// eslint-disable-next-line no-underscore-dangle
const nodeRev = n => n._rev || 0;
const nodesSignature = list =>
  `${list.length}|${list.map(n => `${n.id}:${n.position?.x},${n.position?.y}:${n.type}:${nodeRev(n)}`).join(';')}`;
const edgesSignature = list =>
  `${list.length}|${list.map(e => `${e.id}:${e.source}>${e.target}:${e.sourceHandle || ''}|${e.targetHandle || ''}`).join(';')}`;

let lastNodesSig = nodesSignature(nodes.value);
let lastEdgesSig = edgesSignature(edges.value);

// Sync nodes from Vuex store → local nodes ref (which is bound via
// v-model:nodes). v-model handles add/remove correctly. For data-only
// mutations on existing nodes (e.g. SwitchNode adding cases), we also
// call updateNodeData() because v-model's diff replaces position+id+
// type but NOT data — the prior _rev-aware reassignment below would
// produce a new array reference, but Vue Flow's internal reconcile
// keeps the original data object for existing ids.
const lastRevByNode = new Map(storeNodes.value.map(n => [n.id, nodeRev(n)]));
watch(
  storeNodes,
  newStoreNodes => {
    const mapped = newStoreNodes.map(toVueFlowNode);
    const sig = nodesSignature(mapped);
    if (sig !== lastNodesSig) {
      lastNodesSig = sig;
      nodes.value = mapped;
    }
    // Reconcile data deltas via updateNodeData — Vue Flow's v-model
    // diff doesn't touch data on existing ids, so SwitchNode handles
    // and set_variable badges would otherwise stay stale.
    newStoreNodes.forEach(n => {
      const rev = nodeRev(n);
      const prev = lastRevByNode.get(n.id);
      if (prev === undefined || rev !== prev) {
        lastRevByNode.set(n.id, rev);
        if (findNode(n.id)) {
          updateNodeData(n.id, { ...n.data });
        }
      }
    });
    if (lastRevByNode.size !== newStoreNodes.length) {
      const liveIds = new Set(newStoreNodes.map(n => n.id));
      lastRevByNode.forEach((_, id) => {
        if (!liveIds.has(id)) lastRevByNode.delete(id);
      });
    }
  },
  { deep: true }
);

// Same Vue Flow quirk as the nodes watcher: reassigning edges.value via
// v-model:edges does NOT trigger Vue Flow's internal edge store. Edges
// created via store.dispatch('connectNodes') (e.g. from CommandPalette,
// templates, or paste) silently disappear from the canvas even though
// they're in the store. We reconcile by diffing the per-id maps and
// calling addEdges / removeEdges / updateEdge on the live VueFlow
// instance — which mutates its internal store in place.
//
// Critical: addEdges is wrapped in nextTick. When a user creates a node
// and immediately connects it (the common case for templates / undo /
// dispatch-driven creation), the nodes watcher and the edges watcher
// both fire in the same Vue tick — addEdges then runs before Vue Flow
// has flushed addNodes into its internal Map and rejects the edge with
// "Edge source or target is missing". One frame delay is enough.
// Sync edges from Vuex store → local edges ref (v-model:edges).
// Direct assignment (no nextTick) — the previous nextTick attempt
// didn't help because Vue Flow validates edges against the same-tick
// state of `nodes` array, which is already up-to-date by the time
// the connections watcher fires (nodes always commit before edges
// in our action flow). The "edge needs source/target" warning was
// caused by edges referencing nodes that don't exist in the store
// (e.g. user typed a wrong id), NOT by a timing race.
// Track per-edge _rev so we can imperatively call updateEdge() on Vue
// Flow's internal store when label / animated change on an existing
// edge id. v-model:edges diffs by id and never replaces the existing
// edge object, so without this the label edit would update the store
// but never appear on the canvas.
const edgeRev = e => e._rev || 0; // eslint-disable-line no-underscore-dangle
const lastEdgeRevById = new Map(
  storeConnections.value.map(c => [c.id, edgeRev(c)])
);
watch(
  storeConnections,
  newStoreConnections => {
    const mapped = newStoreConnections.map(toVueFlowEdge);
    const sig = edgesSignature(mapped);
    if (sig !== lastEdgesSig) {
      lastEdgesSig = sig;
      edges.value = mapped;
    }
    // Imperative reconcile for label/animated changes on existing ids.
    // Direct property mutation on the live edge object — Vue Flow's
    // updateEdge() is for replacing source/target/handles, not for
    // metadata; updateEdgeData() only touches edge.data. The Edge
    // component watches each edge's properties reactively, so direct
    // assignment is enough.
    newStoreConnections.forEach(c => {
      const rev = edgeRev(c);
      const prev = lastEdgeRevById.get(c.id);
      if (prev === undefined || rev !== prev) {
        lastEdgeRevById.set(c.id, rev);
        const live = findEdge(c.id);
        if (live) {
          live.label = c.label ?? '';
          live.animated = c.animated !== false;
        }
      }
    });
    // Drop tracking entries for removed edges
    if (lastEdgeRevById.size !== newStoreConnections.length) {
      const liveIds = new Set(newStoreConnections.map(c => c.id));
      lastEdgeRevById.forEach((_, id) => {
        if (!liveIds.has(id)) lastEdgeRevById.delete(id);
      });
    }
  },
  { deep: true }
);

// Validate the candidate connection BEFORE Vue Flow accepts the drop.
// Returning false aborts the drop (the edge is never created and Vue
// Flow shows a "no-drop" cursor). Same rules are enforced in the
// store mutation as a defense-in-depth — this is just the UX layer.
//
// CRITICAL: this callback is ALSO invoked by Vue Flow's setEdges()
// during prop reactivity sync — including for edges that were created
// programmatically (via store.dispatch('connectNodes') from templates,
// undo/redo, command palette). At that point the edge is already in
// `storeConnections.value`, so a dedupe check that compares against
// the store would match the edge against itself and silently kill it
// with EDGE_INVALID "An edge needs a source and a target". Skip dedupe
// here — addConnection mutation already dedupes; Vue Flow's
// connectionExists handles drag-time UX.
const findNodeById = id => storeNodes.value.find(n => n.id === id);
const isValidConnection = (connection, contextEdges) => {
  // Self-loops never make sense in our exec model.
  if (connection.source === connection.target) return false;

  const sourceNode = findNodeById(connection.source);
  const targetNode = findNodeById(connection.target);
  if (!sourceNode || !targetNode) return false;

  // Triggers cannot have inputs (they are the entry point).
  if (targetNode.type === 'trigger') return false;

  // Annotations are visual-only — neither source nor target of edges.
  if (sourceNode.type === 'annotation' || targetNode.type === 'annotation')
    return false;

  // Drag-time dedupe: Vue Flow passes its CURRENT edges (excluding the
  // candidate being dragged) as the second arg. Only block if a real
  // duplicate already exists from this user's perspective — never block
  // against the store, which races with addConnection commits.
  const liveEdges = contextEdges?.edges || [];
  const dup = liveEdges.some(
    e =>
      e.source === connection.source &&
      e.target === connection.target &&
      (e.sourceHandle ?? null) === (connection.sourceHandle ?? null) &&
      (e.targetHandle ?? null) === (connection.targetHandle ?? null) &&
      e.id !== connection.id // never match self when reapplied
  );
  if (dup) return false;

  return true;
};

// New edge created by user dragging from a source handle to a target handle.
// Forward to our store action (which already dedupes + records history).
onConnect(connection => {
  store.dispatch('automationFlow/connectNodes', {
    sourceId: connection.source,
    targetId: connection.target,
    sourceHandle: connection.sourceHandle,
    targetHandle: connection.targetHandle,
  });
});

// Persist position after a drag completes (not every frame — perf + UX:
// undo restores to the drop point, not to one of the intermediate
// frames).
onNodeDragStop(({ node }) => {
  store.commit('automationFlow/updateNode', {
    nodeId: node.id,
    updates: { position: node.position },
    addToHistory: true,
  });
});

// Vue Flow emits an edges-change event with `type: 'remove'` when the
// user presses Delete/Backspace on a selected edge. Forward removals to
// our store so the source-of-truth stays in sync.
onEdgesChange(changes => {
  changes
    .filter(c => c.type === 'remove')
    .forEach(c => {
      store.commit('automationFlow/removeConnection', c.id);
    });
});

// Right-click on an edge → context menu with: edit label, delete.
const edgeContextMenu = ref({
  open: false,
  edgeId: null,
  x: 0,
  y: 0,
  label: '',
});
const labelInputRef = ref(null);

onEdgeContextMenu(({ event, edge }) => {
  event.preventDefault();
  event.stopPropagation();
  edgeContextMenu.value = {
    open: true,
    edgeId: edge.id,
    x: event.clientX,
    y: event.clientY,
    label: edge.label || '',
  };
  nextTick(() => labelInputRef.value?.focus());
});

const closeEdgeMenu = () => {
  edgeContextMenu.value = { open: false, edgeId: null, x: 0, y: 0, label: '' };
};

const saveEdgeLabel = () => {
  if (!edgeContextMenu.value.edgeId) return;
  store.commit('automationFlow/updateConnection', {
    connectionId: edgeContextMenu.value.edgeId,
    updates: { label: edgeContextMenu.value.label.trim() },
    addToHistory: true,
  });
  closeEdgeMenu();
};

const deleteEdgeFromMenu = () => {
  if (!edgeContextMenu.value.edgeId) return;
  store.commit('automationFlow/removeConnection', edgeContextMenu.value.edgeId);
  closeEdgeMenu();
};

// Position the menu at the click coords, with viewport clamping so it
// doesn't render off-screen near the canvas edges.
const edgeMenuPositionStyle = computed(() => {
  const { x, y } = edgeContextMenu.value;
  const maxX = (typeof window !== 'undefined' ? window.innerWidth : 1920) - 260;
  const maxY =
    (typeof window !== 'undefined' ? window.innerHeight : 1080) - 200;
  return {
    top: `${Math.min(y, maxY)}px`,
    left: `${Math.min(x, maxX)}px`,
  };
});

// ── Drag-and-drop from FlowToolbar ─────────────────────────────────────────
// FlowToolbar items set `application/flow-builder-node` DataTransfer with a
// JSON payload describing the node to create.
const canvasContainerRef = ref(null);

const handleDragOver = event => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.dataTransfer.dropEffect = 'copy';
};

const handleDrop = event => {
  event.preventDefault();
  const raw = event.dataTransfer.getData('application/flow-builder-node');
  if (!raw) return;
  let nodeConfig;
  try {
    nodeConfig = JSON.parse(raw);
  } catch {
    return;
  }
  if (!nodeConfig?.type) return;

  // Project the drop coordinates from screen-space into Vue Flow's
  // internal coordinate system (accounts for pan + zoom).
  const bounds = canvasContainerRef.value?.getBoundingClientRect();
  if (!bounds) return;
  const position = project({
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  });

  // Snap to a 20px grid (Vue Flow's snapToGrid prop does its own internal
  // snap during drag; we mirror here so the drop point matches the
  // post-drag rest position).
  const grid = 20;
  const snapped = {
    x: Math.round(position.x / grid) * grid,
    y: Math.round(position.y / grid) * grid,
  };

  store.commit('automationFlow/addNode', {
    id: `${nodeConfig.type}_${Date.now()}`,
    type: nodeConfig.type,
    position: snapped,
    data: nodeConfig.data || {},
  });
  store.commit('automationFlow/recordCurrentState');
};

// ── Command palette (Cmd+K) — state declared early so handleKeyShortcuts
//    can reference it without violating no-use-before-define ───────────────
const isCommandPaletteOpen = ref(false);

// ── Copy / paste / duplicate keyboard shortcuts ───────────────────────────
// Vue Flow tracks `selected: true` on nodes the user has clicked or
// lasso-selected. We mirror n8n's behavior: Ctrl/Cmd+C copies all
// selected nodes (plus internal edges between them) to an in-memory
// clipboard; Ctrl/Cmd+V pastes at a 40px offset; Ctrl/Cmd+D is a
// one-shot copy+paste of whatever is selected.
const PASTE_OFFSET = 40;
const clipboard = ref({ nodes: [], edges: [] });

const isEditingTarget = target => {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
};

const getSelectedStoreNodes = () => {
  const selectedIds = new Set(
    nodes.value.filter(n => n.selected).map(n => n.id)
  );
  return storeNodes.value.filter(n => selectedIds.has(n.id));
};

const handleCopy = () => {
  const selected = getSelectedStoreNodes();
  if (selected.length === 0) return;
  const selectedIds = new Set(selected.map(n => n.id));
  // Include only edges that are FULLY contained in the selection;
  // dangling edges would point to nothing on paste.
  const internalEdges = storeConnections.value.filter(
    e => selectedIds.has(e.source) && selectedIds.has(e.target)
  );
  clipboard.value = {
    nodes: JSON.parse(JSON.stringify(selected)),
    edges: JSON.parse(JSON.stringify(internalEdges)),
  };
};

const handlePaste = () => {
  if (clipboard.value.nodes.length === 0) return;
  // Generate fresh ids so the paste creates new nodes (vs. moving the
  // originals). Build an id remap so internal edges stay connected.
  const ts = Date.now();
  const idRemap = new Map();
  clipboard.value.nodes.forEach((n, i) => {
    idRemap.set(n.id, `${n.type}_${ts}_${i}`);
  });

  clipboard.value.nodes.forEach(n => {
    store.commit('automationFlow/addNode', {
      ...JSON.parse(JSON.stringify(n)),
      id: idRemap.get(n.id),
      position: {
        x: (n.position?.x || 0) + PASTE_OFFSET,
        y: (n.position?.y || 0) + PASTE_OFFSET,
      },
    });
  });

  clipboard.value.edges.forEach(e => {
    store.dispatch('automationFlow/connectNodes', {
      sourceId: idRemap.get(e.source),
      targetId: idRemap.get(e.target),
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      label: e.label,
    });
  });

  store.commit('automationFlow/recordCurrentState');
};

const handleDuplicate = () => {
  // Duplicate = copy + paste in one shot, regardless of clipboard state.
  // Preserves whatever was selected before the user pressed the shortcut.
  handleCopy();
  handlePaste();
};

const handleKeyShortcuts = event => {
  if (isEditingTarget(event.target)) return;
  const isMod = event.ctrlKey || event.metaKey;
  if (!isMod) return;

  // Cmd+K opens the command palette (n8n / Linear / Notion pattern).
  // Allowed when no nodes are selected too — this is the "I want to
  // create a node" shortcut, not a "do something to selection" one.
  //
  // stopPropagation is critical: Chatwoot's dashboard root also listens
  // for Cmd+K and opens its own global navigation palette. Without
  // stopping propagation, BOTH palettes overlap on screen.
  if (event.key === 'k' || event.key === 'K') {
    event.preventDefault();
    event.stopPropagation();
    isCommandPaletteOpen.value = true;
    return;
  }

  if (event.key === 'c' || event.key === 'C') {
    if (getSelectedStoreNodes().length === 0) return;
    event.preventDefault();
    handleCopy();
  } else if (event.key === 'v' || event.key === 'V') {
    if (clipboard.value.nodes.length === 0) return;
    event.preventDefault();
    handlePaste();
  } else if (event.key === 'd' || event.key === 'D') {
    if (getSelectedStoreNodes().length === 0) return;
    event.preventDefault();
    handleDuplicate();
  }
};

// ── Command palette (Cmd+K) — handler ──────────────────────────────────────
const handlePaletteSelect = item => {
  // Create the node at the visible canvas center (so the user sees it
  // immediately after pressing Enter). project() converts screen→canvas
  // coords; we use the canvas container's center.
  const bounds = canvasContainerRef.value?.getBoundingClientRect();
  let position;
  if (bounds) {
    position = project({
      x: bounds.width / 2,
      y: bounds.height / 2,
    });
  } else {
    position = { x: 300, y: 200 };
  }
  // Snap to grid
  position = {
    x: Math.round(position.x / 20) * 20,
    y: Math.round(position.y / 20) * 20,
  };

  store.commit('automationFlow/addNode', {
    id: `${item.type}_${Date.now()}`,
    type: item.type,
    position,
    data: { ...item.data, label: item.label },
  });
  store.commit('automationFlow/recordCurrentState');
  isCommandPaletteOpen.value = false;
};

// ── Auto-layout (ELK) ─────────────────────────────────────────────────────
const isAutoLaying = ref(false);
const handleAutoLayout = async () => {
  if (isAutoLaying.value || storeNodes.value.length === 0) return;
  isAutoLaying.value = true;
  try {
    const positions = await computeAutoLayout(
      storeNodes.value,
      storeConnections.value
    );
    // Apply all positions in a single bulk update. The final
    // recordCurrentState below makes the whole layout one undo step.
    positions.forEach((position, nodeId) => {
      store.commit('automationFlow/updateNode', {
        nodeId,
        updates: { position },
        addToHistory: false,
      });
    });
    store.commit('automationFlow/recordCurrentState');
    // Defer fit so Vue Flow re-renders with new positions first.
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50);
    showAlert(
      t('PIPELINE.FLOW_BUILDER.CANVAS.AUTO_LAYOUT_SUCCESS', 'Layout aplicado')
    );
  } catch {
    showAlert(
      t(
        'PIPELINE.FLOW_BUILDER.CANVAS.AUTO_LAYOUT_ERROR',
        'Não foi possível aplicar o layout'
      )
    );
  } finally {
    isAutoLaying.value = false;
  }
};

// ── Loading guard — only fit on initial mount with nodes present ──────────
let didInitialFit = false;
const maybeInitialFit = () => {
  if (didInitialFit) return;
  if (storeNodes.value.length === 0) return;
  didInitialFit = true;
  // Defer to next tick so Vue Flow has rendered the nodes' dimensions.
  setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50);
};

watch(storeNodes, maybeInitialFit, { immediate: true });

onMounted(() => {
  maybeInitialFit();
  // `true` = capture phase. Chatwoot's app-level keydown listener for the
  // global Cmd+K palette is registered at app boot, so it sits ahead of ours
  // in bubble phase. Capturing first lets us stopPropagation() on Cmd+K
  // before Chatwoot ever sees it.
  document.addEventListener('keydown', handleKeyShortcuts, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyShortcuts, true);
});
</script>

<template>
  <div ref="canvasContainerRef" class="w-full h-full relative bg-n-background">
    <VueFlow
      :id="FLOW_ID"
      v-model:nodes="nodes"
      v-model:edges="edges"
      :default-edge-options="{ type: 'smoothstep', animated: true }"
      :delete-key-code="['Delete', 'Backspace']"
      :is-valid-connection="isValidConnection"
      snap-to-grid
      :snap-grid="[20, 20]"
      :min-zoom="0.3"
      :max-zoom="2"
      :node-types="{
        trigger: TriggerNode,
        action: ActionNode,
        condition: ConditionNode,
        switch: SwitchNode,
        annotation: AnnotationNode,
      }"
      fit-view-on-init
      @dragover="handleDragOver"
      @drop="handleDrop"
    >
      <!-- Dot pattern background — replaces the hand-rolled radial gradient
           on the old canvas. -->
      <Background :gap="20" :size="1" />

      <!-- Built-in controls (zoom +/-, fit, lock) — replaces the custom
           zoom controls. -->
      <Controls position="bottom-right" :show-interactive="false">
        <ControlButton
          :title="t('PIPELINE.FLOW_BUILDER.CANVAS.FIT_SCREEN')"
          @click="fitView({ padding: 0.2, duration: 300 })"
        >
          <span class="i-lucide-maximize-2 size-4" aria-hidden="true" />
        </ControlButton>
        <ControlButton
          :title="
            t('PIPELINE.FLOW_BUILDER.CANVAS.AUTO_LAYOUT', 'Auto-arranjar')
          "
          :disabled="isAutoLaying || nodes.length === 0"
          @click="handleAutoLayout"
        >
          <span
            :class="
              isAutoLaying
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-layout-grid'
            "
            class="size-4"
            aria-hidden="true"
          />
        </ControlButton>
      </Controls>

      <!-- Built-in minimap — replaces the custom FlowMinimap.vue. Only
           appears when 2+ nodes exist, matching the old behavior. -->
      <MiniMap
        v-if="nodes.length >= 2"
        position="bottom-left"
        pannable
        zoomable
        :node-color="
          n => {
            if (n.type === 'trigger') return 'rgb(var(--blue-9))';
            if (n.type === 'condition') return 'rgb(var(--amber-9))';
            if (n.type === 'action') return 'rgb(var(--teal-9))';
            if (n.type === 'annotation') return 'rgb(var(--amber-5))';
            return 'rgb(var(--slate-6))';
          }
        "
        mask-color="rgba(0, 0, 0, 0.05)"
      />

      <!-- Empty state — only renders when no nodes. pointer-events-none so
           it doesn't block drop targets. -->
      <div
        v-if="!nodes.length && !isLoading"
        class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center z-10"
      >
        <div
          class="size-20 rounded-2xl bg-n-alpha-1 flex items-center justify-center mb-4 ring-2 ring-dashed ring-n-weak"
        >
          <span
            class="i-lucide-workflow size-9 text-n-slate-10"
            aria-hidden="true"
          />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1">
          {{ t('PIPELINE.FLOW_BUILDER.CANVAS.EMPTY_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-xs">
          {{ t('PIPELINE.FLOW_BUILDER.CANVAS.EMPTY_DESCRIPTION') }}
        </p>
        <div class="mt-3 flex items-center gap-1.5 text-xs text-n-slate-10">
          <span class="i-lucide-mouse-pointer-2 size-3" aria-hidden="true" />
          <span>{{ t('PIPELINE.FLOW_BUILDER.CANVAS.EMPTY_HINT') }}</span>
        </div>
      </div>

      <!-- Loading skeleton — overlay while the parent FlowBuilder loads
           the automation from the API. -->
      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-n-background/60 backdrop-blur-sm z-20"
      >
        <span
          class="i-lucide-loader-2 size-8 text-n-slate-10 animate-spin"
          aria-hidden="true"
        />
      </div>
    </VueFlow>

    <CommandPalette
      :open="isCommandPaletteOpen"
      @close="isCommandPaletteOpen = false"
      @select="handlePaletteSelect"
    />

    <!-- Edge context menu (right-click on a connection): edit label + delete.
         Positioned at the click coordinates via inline CSS (no other way to
         do dynamic positioning without binding :style). -->
    <Teleport v-if="edgeContextMenu.open" to="body">
      <div
        class="fixed inset-0 z-[100]"
        @click="closeEdgeMenu"
        @contextmenu.prevent="closeEdgeMenu"
      >
        <div
          class="absolute bg-n-solid-2 rounded-lg shadow-xl outline outline-1 outline-n-weak p-3 min-w-[240px]"
          :style="edgeMenuPositionStyle"
          @click.stop
        >
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PIPELINE.FLOW_BUILDER.CANVAS.EDGE_LABEL_FIELD') }}
          </label>
          <input
            ref="labelInputRef"
            v-model="edgeContextMenu.label"
            type="text"
            class="w-full px-2 py-1 text-sm bg-n-background border border-n-weak rounded text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.CANVAS.EDGE_LABEL_PLACEHOLDER')
            "
            @keydown.enter="saveEdgeLabel"
            @keydown.escape="closeEdgeMenu"
          />
          <div class="flex items-center justify-between mt-3 gap-2">
            <button
              type="button"
              class="px-2 py-1 text-xs text-ruby-11 hover:text-ruby-12 hover:bg-ruby-3 rounded flex items-center gap-1"
              @click="deleteEdgeFromMenu"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
              {{ t('PIPELINE.FLOW_BUILDER.CANVAS.EDGE_DELETE') }}
            </button>
            <div class="flex gap-2">
              <button
                type="button"
                class="px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-1 rounded"
                @click="closeEdgeMenu"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL') }}
              </button>
              <button
                type="button"
                class="px-2 py-1 text-xs text-white bg-n-brand hover:opacity-90 rounded"
                @click="saveEdgeLabel"
              >
                {{ t('PIPELINE.FLOW_BUILDER.EDITOR.SAVE') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
/* Theme overrides to align Vue Flow defaults with NooviChat tokens.
   Vue Flow CSS variables are documented at
   https://vueflow.dev/guide/theming.html — overriding here keeps
   theme switching (light/dark) free since our tokens are already
   --color-* based. */
.vue-flow {
  --vf-node-color: rgb(var(--slate-12));
  --vf-node-bg: rgb(var(--solid-2));
  --vf-node-text: rgb(var(--slate-12));
  --vf-connection-path: rgb(var(--slate-9));
  --vf-handle: rgb(var(--slate-9));
  --vf-handle-active: #2781f6;
  background: transparent;
}

/* Vue Flow draws the node outer div with white background by default — we
   override to transparent so our card (rendered by BaseNode) controls the
   visible chrome. */
.vue-flow__node {
  background: transparent;
  border: none;
  padding: 0;
  width: auto;
}

/* Selected edge — recolor the bezier path to brand. */
.vue-flow__edge.selected .vue-flow__edge-path,
.vue-flow__edge:focus .vue-flow__edge-path,
.vue-flow__edge:focus-visible .vue-flow__edge-path {
  stroke: #2781f6;
  stroke-width: 2.5;
}

/* Edge default — slightly heavier than Vue Flow's default for visibility
   on the dot pattern background. */
.vue-flow__edge-path {
  stroke: rgb(var(--slate-9));
  stroke-width: 2;
}

/* Built-in Controls panel — align background with our solid-2 token. */
.vue-flow__controls {
  background: rgb(var(--solid-2));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.vue-flow__controls-button {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgb(var(--border-weak));
  color: rgb(var(--slate-11));
}
.vue-flow__controls-button:hover {
  background: rgba(var(--alpha-1));
}
.vue-flow__controls-button:last-child {
  border-bottom: none;
}

/* Minimap — soften default border for our shadow style. */
.vue-flow__minimap {
  background: rgb(var(--solid-2));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
</style>
